// StudentSocketProvider.jsx
// Monta la conexión WebSocket del Ztudiante SOLO cuando:
// - Hay sesión válida en sessionStorage
// - centerId === 9999 Ejemplo
// - La ruta actual está en la lista permitida (whitelist)
//
// Integra este Provider alto en tu árbol (ej. en App.jsx), para que cubra las páginas
// donde el Ztudiante navega después del login. En Login NO se conecta.

import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
//import { connectStudent, disconnectStudent, isReady, setMessageHandler, clearMessageHandler, send } from './../../utilities/BL/Student/ZtudentView'
import * as WsStudent from './../../utilities/BL/Student/ZtudentView'

// Rutas donde SÍ queremos conectar el socket del Ztudiante.
// Puedes agregar más (usa exacto o startsWith más abajo).
const ALLOWED_PATHS = [
  '/results/items',
  '/results',
  '/viewer',
  '/course',
  '/dashboard',
  '/confirm',
  '/instructor/ztudentView'
]

const LOGIN_PATH = '/'              // tu ruta de login
const LOGOUT_EVENT = 'app:logout'   // evento propio para logout en mismo tab

const readSessionBasicsSafe = () => {
  try {
    const raw = sessionStorage.getItem(process.env.REACT_APP_SESSION)
    if (!raw) {
      // No hay sesión: devolver objeto “vacío” seguro
      return { hasSession: false, centerId: null, username: null, name: '', lastName: '' }
    }

    const s = JSON.parse(raw)
    const nav = s?.navigation ?? {}

    return {
      hasSession: true,
      centerId: Number(nav?.center?.id ?? NaN), // NaN si no viene
      username: nav?.username ?? null,
      name: s?.user?.name ?? '',
      lastName: s?.user?.lastName ?? '',
      role: nav.role.id
    }
  } catch {
    // JSON inválido o cualquier otra cosa: tratar como sin sesión
    return { hasSession: false, centerId: null, username: null, name: '', lastName: '' }
  }
}

// Lee y valida lo mínimo necesario de la sesión
const readSessionBasics = () => {
  try {
    const raw = sessionStorage.getItem(process.env.REACT_APP_SESSION)
    if (!raw) return null
    const s = JSON.parse(raw)

    const nav = s.navigation || {}
    const centerId = Number(nav?.center?.id)
    const username = nav?.username || null
    const user = s.user || {}

    return {
      centerId,
      username,
      name: user?.name || '',
      lastName: user?.lastName || ''
    }
  } catch {
    return null
  }
}

// ¿La ruta actual está permitida?
const isAllowedPath = (pathname) => {
  if (!pathname) return false
  // evita conectar en login
  if (pathname === LOGIN_PATH) return false
  return ALLOWED_PATHS.some(p => pathname === p || pathname.startsWith(p + '/'))
}

// export function StudentSocketProvider({ children }) {
//   const location = useLocation()
//   const bootstrappedRef = useRef(false)

//   //console.log('[StudentSocketProvider] montado');

//   useEffect(() => {
//     bootstrappedRef.current = true

//     const tryConnectOrDisconnect = () => {
//       const pathname = location?.pathname || '/'
//       const session = readSessionBasics()

//       // Si no es una ruta habilitada, desconecta y sal
//       if (!isAllowedPath(pathname)) {
//         //console.warn('[StudentSocket] ruta no permitida')
//         WsStudent.disconnectStudent()
//         return
//       }

//       if (!session) {
//         //console.warn('[StudentSocket] no hay sesión')
//       }

//       // Si no hay sesión o no es centerId 9999, desconecta y sal
//       //if (!session || session.centerId !== 9999 || !session.username) {
//       //if (!session || session.centerId !== 2 || !session.username) {
//       //if (session && session.centerId !== 2) { // Funcional
//       if (session && (session.centerId !== 2 && session.centerId !== 318)) {
//         //console.warn('[StudentSocket] centro distinto a 9999')
//         WsStudent.disconnectStudent()
//         return
//       }

//       if (session && !session.username) {
//         //console.warn('[StudentSocket] falta username')
//       }

//       // console.log('[StudentSocket] path=', pathname)
//       // console.log('[StudentSocket] allowed?', isAllowedPath(pathname))
//       // console.log('[StudentSocket] centerId=', session?.centerId, 'username=', session?.username)

//       // Tenemos todo para conectar
//       WsStudent.connectStudent({
//         centerId: session.centerId,
//         username: session.username,
//         name: session.name,
//         lastName: session.lastName
//       })
//     }

//     // 1) Intento inmediato cada vez que cambia la ruta
//     tryConnectOrDisconnect()

//     // 2) Reaccionar cuando el login (u otro flujo) setea la sesión en sessionStorage
//     const onStorage = (e) => {
//       if (e.key === process.env.REACT_APP_SESSION) {
//         tryConnectOrDisconnect()
//       }
//     }
//     window.addEventListener('storage', onStorage)

//     // 3) Pequeño poll de gracia (6s) para el caso de setItem en el MISMO tab
//     const poll = setInterval(tryConnectOrDisconnect, 1000)
//     const stopPoll = setTimeout(() => clearInterval(poll), 6000)

//     return () => {
//       window.removeEventListener('storage', onStorage)
//       clearTimeout(stopPoll)
//       clearInterval(poll)
//       // No desconectamos aquí de golpe; la desconexión se decide por ruta/sesión arriba.
//     }
//   }, [location?.pathname])

//   return children
// }

const StudentSocketProvider = ({ children }) => {
  const location = useLocation()
  const pollRef = useRef(null)

  useEffect(() => {
    const tryConnectOrDisconnect = () => {
      const pathname = location?.pathname || '/'
      // 1) si la ruta no es permitida, desconecta
      if (!isAllowedPath(pathname)) {
        WsStudent.disconnectStudent()
        return
      }

      // 2) lee sesión de forma segura
      const s = readSessionBasicsSafe()

      // 3) si no hay sesión válida, desconecta
      if (!s.hasSession || !Number.isFinite(s.centerId) || !s.username) {
        WsStudent.disconnectStudent()
        return
      }

      // 4) filtra por centro permitido
      //if (s.centerId !== 9999) { // Ejemplo
      if (s.centerId !== 2 && s.centerId !== 318) { // Es centro de Five o ISTE
        WsStudent.disconnectStudent()
        return
      }

      // 5) conecta (idempotente: si ya está, no reconecta)
      WsStudent.connectStudent({
        centerId: s.centerId,
        username: s.username,
        name: s.name,
        lastName: s.lastName
      })
    }

    // intento inmediato al cambiar de ruta
    tryConnectOrDisconnect()

    // storage listener (aunque sessionStorage no dispara en mismo tab, lo mantenemos para otros tabs)
    const onStorage = (e) => {
      if (e.key === process.env.REACT_APP_SESSION) {
        tryConnectOrDisconnect()
      }
    }
    window.addEventListener('storage', onStorage)

    // evento propio de logout en el MISMO tab
    const onAppLogout = () => {
      // nos desconectamos y NO intentamos reconectar hasta que cambie ruta o haya nueva sesión
      WsStudent.disconnectStudent()
    }
    window.addEventListener(LOGOUT_EVENT, onAppLogout)

    // pequeño poll de gracia (máx 6s) por si la sesión se setea asíncronamente
    pollRef.current = setInterval(tryConnectOrDisconnect, 1000)
    const stopPollTimer = setTimeout(() => {
      clearInterval(pollRef.current)
      pollRef.current = null
    }, 6000)

    return () => {
      window.removeEventListener('storage', onStorage)
      window.removeEventListener(LOGOUT_EVENT, onAppLogout)
      clearTimeout(stopPollTimer)
      if (pollRef.current) {
        clearInterval(pollRef.current)
        pollRef.current = null
      }
      // No forzamos disconnect aquí; lo controla la lógica arriba según ruta/sesión.
    }
  }, [location?.pathname])

  return children
}

export { readSessionBasicsSafe, StudentSocketProvider };










// // =====================================================
// //  StudentSocketProvider
// //  - Inicia el WS del Ztudiante apenas carga la app
// //  - Mantiene estado simple: status, lastEvent
// //  - Propaga mensajes por Context (opcional usarlo)
// //  - Cierra WS al salir del sitio (beforeunload)
// //  - NO duplica conexiones (usa el singleton WsStudent)
// // =====================================================

// import React, { createContext, useContext, useEffect, useRef, useState } from 'react'
// import { connectStudent, disconnectStudent, isReady, setMessageHandler, clearMessageHandler, send } from './../../utilities/BL/Student/ZtudentView'

// // ---------- Context ----------
// const StudentSocketContext = createContext({
//   status: 'idle',     // idle | connecting | connected | reconnecting | error
//   lastEvent: null,    // último mensaje recibido (para debug/UI)
//   isReady: () => false,
//   send: () => false,
// })

// export const useStudentSocket = () => useContext(StudentSocketContext)

// // ---------- Provider ----------
// export const StudentSocketProvider = ({ children }) => {
//   const [status, setStatus] = useState('idle')
//   const [lastEvent, setLastEvent] = useState(null)
//   const mountedRef = useRef(false)

//   // Handler general de mensajes (opcional para UI)
//   const onMessage = (msg) => {
//     // Guardamos el último por si quieres depurarlo en algún panel
//     setLastEvent(msg)

//     // No necesitas hacer nada para "request-snapshot":
//     // WsStudent ya responde automáticamente con el snapshot del viewport.
//     // Aquí puedes reaccionar si quieres (ej: mostrar un toast).
//     switch (msg?.type) {
//       case 'pong':
//         // ejemplo: console.debug('[Ztudiante] pong', msg.ts)
//         break
//       case 'error':
//         // ejemplo: console.warn('[Ztudiante] error', msg)
//         break
//       default:
//         break
//     }
//   }

//   // Arrancar conexión una sola vez para TODA la app
//   useEffect(() => {
//     if (mountedRef.current) return
//     mountedRef.current = true

//     setStatus('connecting')
//     // Abre conexión (sólo si center.id === 9999; lo valida el módulo)
//     connectStudent()

//     // Registrar handler de mensajes
//     setMessageHandler(onMessage)

//     // Monitoreo de estado de forma naïve (opcional)
//     const t = setInterval(() => {
//       setStatus(prev => {
//         // si está listo => connected
//         if (isReady()) return 'Conectado'
//         // si no está listo, podríamos estar reconectando
//         // no hay forma directa de leer "reconnecting" del singleton;
//         // mantenemos 'connecting' si aún no se logró la primera conexión
//         return prev === 'Conectado' ? 'Reconectando...' : 'Conectando...'
//       })
//     }, 1500)

//     // Cierre ordenado al salir de la página
//     const onBeforeUnload = () => {
//       clearInterval(t)
//       clearMessageHandler()
//       disconnectStudent()
//     }
//     window.addEventListener('beforeunload', onBeforeUnload)

//     return () => {
//       clearInterval(t)
//       clearMessageHandler()
//       disconnectStudent()
//       window.removeEventListener('beforeunload', onBeforeUnload)
//     }
//   }, [])

//   const value = {
//     status,
//     lastEvent,
//     isReady,
//     send, // por si en un futuro quieres enviar algo desde el front del ztudiante
//   }

//   return (
//     <StudentSocketContext.Provider value={value}>
//       {children}
//     </StudentSocketContext.Provider>
//   )
// }